import React, { useCallback } from "react"
import { Text } from "@chakra-ui/react"
import { BadgeElement as TBadgeElement, BadgeElementType } from "sharedTypes/badge"
import CanvasObject, {
  CanvasObjectBody, CanvasObjectLabel, Resizable, Rotatable,
} from "components/CanvasEditor/CanvasObject"
import { useSelection } from "components/CanvasEditor/Editor"
import BadgeElement from "../Elements/BadgeElement"
import { EventData, GuestData } from "../utils/badgeDataResolver"
import { getQRCodeMinSize } from "../utils/badgeHelpers"
import { formatMeasurement } from "../utils/units"
import { useBadgeInlineEditing } from "../contexts/BadgeSelectionContext"
import { useBadgeUI } from "../contexts/BadgeUIContext"
import { HitPoint, getBorderStyle, isClickOnShapeVisualPart } from "../utils/elementDisplay"

type Props = {
  element: TBadgeElement
  scaleFactor: number
  guestData?: GuestData
  eventData?: EventData
  onSelect: (elementId: string, shiftKey?: boolean) => void
  onContextMenu?: (elementId: string, e: React.MouseEvent) => void
}

/** A badge element on the canvas, with the badge's rules for selecting and resizing it. */
const BadgeObject = ({
  element,
  scaleFactor,
  guestData,
  eventData,
  onSelect,
  onContextMenu,
}: Props) => {
  const { startInlineEditing, inlineEditingElementId } = useBadgeInlineEditing()
  // Each element watches its own place in the selection, so a click re-renders only those it
  // changes
  const selected = useSelection((state) => state.selectedIds.includes(element.id))
  const onlySelected = useSelection(
    (state) => state.selectedIds.length === 1 && state.selectedIds[0] === element.id,
  )
  const { displayUnit } = useBadgeUI()

  const isQRCode = element.type === BadgeElementType.QR_CODE
  const isText = element.type === BadgeElementType.TEXT
  // Unfilled rectangles and ovals react near their stroke only
  const isOutlineShape = element.type === BadgeElementType.RECTANGLE
    || element.type === BadgeElementType.OVAL
  const flipHorizontal = element.transforms?.flipHorizontal || false
  const flipVertical = element.transforms?.flipVertical || false

  const hitTest = useCallback(
    (point: HitPoint) => isClickOnShapeVisualPart(point, element),
    [element],
  )
  const handleRepeatClick = useCallback(
    () => startInlineEditing(element.id),
    [startInlineEditing, element.id],
  )

  return (
    <CanvasObject
      id={element.id}
      x={element.x}
      y={element.y}
      width={element.width}
      height={element.height}
      rotation={element.transforms?.rotate || 0}
      zIndex={element.zIndex}
      mirror={{ horizontal: flipHorizontal, vertical: flipVertical }}
      selected={selected}
      locked={inlineEditingElementId === element.id}
      hitTest={isOutlineShape ? hitTest : undefined}
      onSelect={onSelect}
      onRepeatClick={isText ? handleRepeatClick : undefined}
      onContextMenu={onContextMenu && ((e) => onContextMenu(element.id, e))}
    >
      <CanvasObjectBody backgroundColor={element.fill} {...getBorderStyle(element, scaleFactor)}>
        {(rect) => (
          <BadgeElement
            element={{ ...element, ...rect }}
            scaleFactor={scaleFactor}
            guestData={guestData}
            eventData={eventData}
          />
        )}
      </CanvasObjectBody>
      <Resizable
        square={isQRCode}
        minSize={isQRCode && "format" in element.data ? getQRCodeMinSize(element.data.format) : 1}
      />
      <Rotatable />
      {onlySelected && (
        <CanvasObjectLabel>
          {(rect) => (
            <Text color="blue.500" whiteSpace="nowrap" fontSize="sm">
              {`${formatMeasurement(rect.width, displayUnit)} × ${formatMeasurement(rect.height, displayUnit)}`}
            </Text>
          )}
        </CanvasObjectLabel>
      )}
    </CanvasObject>
  )
}

export default React.memo(BadgeObject)
